import { Router } from '../router';
import { StreakManager } from '../storage/streakManager';
import { ExplorerPointsManager } from '../storage/explorerPoints';
import { ProfileManager } from '../storage/profileManager';
import { ProfileModal } from './components/profileModal';
import { AtelierTour } from './components/atelierTour';
import { ConnectivityBanner } from './components/connectivityBanner';
import { PwaInstallPrompt } from './components/pwaInstallPrompt';
import { FeedbackDrawer } from '../feedback/feedbackDrawer';
import { FeedbackLauncher } from '../feedback/feedbackLauncher';
import { SyncStatusBadge } from './components/syncStatusBadge';

export interface NavItem {
  path: string;
  label: string;
  icon: string;
}

export const NAV_ITEMS: NavItem[] = [
  { path: '#/', label: 'Expedition Map', icon: '🗺️' },
  { path: '#/daily-review', label: 'Daily Review', icon: '🌅' },
  { path: '#/read', label: 'Comprehension', icon: '📖' },
  { path: '#/context-lens', label: 'Context Lens', icon: '🔎' },
  { path: '#/fact-diver', label: 'Fact Diver', icon: '🤿' },
  { path: '#/spelling', label: 'Spelling Reef', icon: '🐚' },
  { path: '#/sprint', label: 'Quest Sprints', icon: '⚡' },
  { path: '#/mock', label: 'Bexley Mock', icon: '🏛️' },
  { path: '#/geometry', label: 'Geometry', icon: '📐' },
  { path: '#/scan', label: 'Schoolwork', icon: '📷' },
  { path: '#/dashboard', label: 'Parent Deck', icon: '📊' },
  { path: '#/gallery', label: 'Studio', icon: '🎨' },
];

export class AppShell {
  private container: HTMLElement;
  private router: Router;
  private connectivityBanner?: ConnectivityBanner;
  private pwaInstallPrompt?: PwaInstallPrompt;
  private feedbackDrawer?: FeedbackDrawer;
  private feedbackLauncher?: FeedbackLauncher;
  private unsubscribeProfile?: () => void;

  constructor(container: HTMLElement, router: Router) {
    this.container = container;
    this.router = router;
  }

  init(): void {
    this.renderSkeleton();
    this.attachHeaderControls();
    this.router.onNavigate(() => this.updateActiveRoute());
    this.updateActiveRoute();

    // Listen for profile changes to refresh telemetry and active station
    this.unsubscribeProfile = ProfileManager.onProfileChange(() => {
      this.updateActiveRoute();
    });

    // Mount PWA offline connectivity banner & install prompt (T-9.1.3, T-9.1.4)
    this.connectivityBanner = new ConnectivityBanner();
    this.container.insertBefore(this.connectivityBanner.getElement(), this.container.firstChild);

    this.pwaInstallPrompt = new PwaInstallPrompt();
    this.container.appendChild(this.pwaInstallPrompt.getElement());

    // Mount In-Website Agentic Feedback Assistant
    this.feedbackDrawer = new FeedbackDrawer();
    this.container.appendChild(this.feedbackDrawer.getElement());

    this.feedbackLauncher = new FeedbackLauncher(this.feedbackDrawer);
    this.container.appendChild(this.feedbackLauncher.getElement());

    // Mount Cross-Device D1 Sync Status Emblem
    const syncMount = this.container.querySelector('#shell-sync-badge-mount');
    if (syncMount) {
      const syncBadge = new SyncStatusBadge();
      syncMount.appendChild(syncBadge.getElement());
    }
  }

  getConnectivityBanner(): ConnectivityBanner | undefined {
    return this.connectivityBanner;
  }

  getPwaInstallPrompt(): PwaInstallPrompt | undefined {
    return this.pwaInstallPrompt;
  }

  getFeedbackDrawer(): FeedbackDrawer | undefined {
    return this.feedbackDrawer;
  }

  destroy(): void {
    this.unsubscribeProfile?.();
    this.connectivityBanner?.destroy();
  }

  private renderSkeleton(): void {
    const isTester = ProfileManager.isTester();
    const streakStatus = StreakManager.checkStatus();

    this.container.innerHTML = `
      <header class="atelier-topbar shell-header">
        <div class="brand-lockup">
          <a href="#/" style="display: flex; align-items: center; gap: 14px; text-decoration: none; color: inherit;">
            <div class="brand-medallion">
              <img src="/assets/atelier/clawey-brass-porthole.png" alt="Clawey" class="atelier-clawey-medallion" style="width: 48px; height: 48px;" />
            </div>
            <div>
              <h1 class="brand-title">Clawey 11+</h1>
              <div class="brand-subtitle">Victorian Deep-Sea Atelier &amp; KS2 Field Guide</div>
            </div>
          </a>
        </div>
        <div class="topbar-controls">
          <button id="shell-feedback-btn" class="touch-target btn-vellum" style="min-height: 44px; padding: 6px 14px; font-size: 0.85rem; border-radius: 999px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-weight: 700; color: var(--morris-indigo);" aria-label="Open Atelier Feedback Inspector">
            💬 Feedback
          </button>
          <button id="shell-tour-btn" class="touch-target btn-vellum" style="min-height: 44px; padding: 6px 14px; font-size: 0.85rem; border-radius: 999px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-weight: 700; color: var(--morris-indigo);" aria-label="Open Victorian Field Guide and Tour">
            🧭 Field Guide
          </button>
          <div id="shell-sync-badge-mount" style="display: inline-flex; align-items: center;"></div>
          <span id="shell-streak-badge" class="learner-badge-pill">
            ${streakStatus.isFrozen ? '⚓ Frozen' : `🔥 ${streakStatus.currentStreak}d Streak`}
          </span>
          <span id="shell-ep-badge" class="learner-badge-pill">
            🧭 ${ExplorerPointsManager.getTotalEp()} EP
          </span>
          <button id="shell-profile-pill" class="touch-target learner-badge-pill" style="
            cursor: pointer;
            background: ${isTester ? 'linear-gradient(180deg, #F0F9FF 0%, #BAE6FD 100%)' : 'linear-gradient(180deg, #FFFDF9 0%, #EFE5CE 100%)'};
            border: 2px solid ${isTester ? '#0284C7' : 'var(--gilded-brass)'};
          " aria-label="Open Learner Profile and Sandbox Switcher">
            ${isTester ? '🧪 <span>Tester Sandbox</span>' : '⭐ <span>Year 4 · Tudor</span>'}
          </button>
        </div>
      </header>

      <div class="atelier-layout layout-container">
        <!-- Tablet & Desktop Navigation Rail -->
        <nav class="atelier-sidebar nav-rail" aria-label="Main Navigation">
          ${NAV_ITEMS.map(
      (item) => `
            <a href="${item.path}" class="nav-item nav-link" data-path="${item.path}" style="touch-action: manipulation;">
              <span class="nav-icon-badge">${item.icon}</span>
              <span>${item.label}</span>
            </a>
          `
    ).join('')}
        </nav>

        <!-- Main Station Stage -->
        <main id="station-stage" class="atelier-stage layout-main" style="touch-action: manipulation;"></main>
      </div>

      <!-- Mobile Bottom Navigation Bar -->
      <nav class="mobile-nav-bar" aria-label="Mobile Navigation">
        ${NAV_ITEMS.slice(0, 5).map(
      (item) => `
          <a href="${item.path}" class="nav-item nav-link" data-path="${item.path}" style="flex-direction: column; padding: 6px; min-width: 48px; text-align: center; font-size: 0.75rem; gap: 2px;">
            <span style="font-size: 1.25rem;">${item.icon}</span>
            <span>${item.label}</span>
          </a>
        `
    ).join('')}
      </nav>
    `;
  }

  private attachHeaderControls(): void {
    // Feedback Drawer Launcher
    const feedbackBtn = this.container.querySelector('#shell-feedback-btn');
    feedbackBtn?.addEventListener('click', () => {
      this.feedbackDrawer?.open();
    });

    // Tour Launcher
    const tourBtn = this.container.querySelector('#shell-tour-btn');
    tourBtn?.addEventListener('click', () => {
      new AtelierTour().show();
    });

    // Profile Switcher Modal Launcher
    const profileBtn = this.container.querySelector('#shell-profile-pill');
    profileBtn?.addEventListener('click', () => {
      new ProfileModal().show();
    });
  }

  updateActiveRoute(): void {
    const { title, content } = this.router.resolve();
    document.title = `${title} — Clawey 11+`;

    const stage = this.container.querySelector<HTMLElement>('#station-stage');
    if (stage) {
      if (typeof content === 'string') {
        stage.innerHTML = content;
      } else {
        stage.innerHTML = '';
        stage.appendChild(content);
      }
    }

    // Update active nav-link highlighting
    const currentHash = window.location.hash || '#/';
    const links = this.container.querySelectorAll<HTMLAnchorElement>('.nav-link');
    links.forEach((link) => {
      const linkPath = link.getAttribute('data-path');
      if (linkPath === currentHash) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });

    // Refresh dynamic header telemetry badges
    const streakEl = this.container.querySelector('#shell-streak-badge');
    if (streakEl) {
      const status = StreakManager.checkStatus();
      streakEl.textContent = status.isFrozen ? '⚓ Frozen' : `🔥 ${status.currentStreak}d Streak`;
    }
    const epEl = this.container.querySelector('#shell-ep-badge');
    if (epEl) {
      epEl.textContent = `🧭 ${ExplorerPointsManager.getTotalEp()} EP`;
    }

    // Refresh profile badge appearance
    const profileBtn = this.container.querySelector<HTMLButtonElement>('#shell-profile-pill');
    if (profileBtn) {
      const isTester = ProfileManager.isTester();
      profileBtn.innerHTML = isTester
        ? '🧪 <span>Tester Sandbox</span>'
        : '⭐ <span>Year 4 · Tudor</span>';
      profileBtn.style.background = isTester
        ? 'linear-gradient(180deg, #F0F9FF 0%, #BAE6FD 100%)'
        : 'linear-gradient(180deg, #FFFDF9 0%, #EFE5CE 100%)';
      profileBtn.style.borderColor = isTester ? '#0284C7' : 'var(--gilded-brass)';
    }
  }

  /**
   * ADHD-Optimised Single-Question Focus Mode (FR-ADHD-004)
   * Toggles suppression of navigation bars and secondary distractions during active answering.
   */
  static setFocusMode(enabled: boolean): void {
    if (enabled) {
      document.body.classList.add('focus-mode-active');
    } else {
      document.body.classList.remove('focus-mode-active');
    }
  }

  static isFocusMode(): boolean {
    return document.body.classList.contains('focus-mode-active');
  }
}
